import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogoFull } from "./Logo";
import { Calendar, Tag, ArrowRight, ArrowLeft, BookOpen } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then(r => {
      setPosts(r.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-[#E5E7EB] bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="blog-logo"><LogoFull size={24} /></Link>
          <nav className="flex items-center gap-6">
            <Link to="/changelog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Changelog</Link>
            <Link to="/login" className="text-[13px] font-medium text-[#0A0A0A] border border-[#0A0A0A] px-4 py-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="blog-login-btn">Log in</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-3 py-1 text-[11px] font-mono uppercase tracking-wider mb-6">
            <BookOpen className="w-3 h-3" />
            The VeroBill Blog
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight leading-[1.1] mb-4">
            Invoicing tips for freelancers who mean business
          </h1>
          <p className="text-base text-[#6B7280] max-w-xl mx-auto">
            Practical guides on getting paid faster, managing clients, and growing your freelance business.
          </p>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="text-center py-16 text-[#9CA3AF]">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#6B7280] text-lg mb-2">No posts yet.</p>
            <p className="text-[#9CA3AF] text-sm">Check back soon for freelancing and invoicing tips.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {posts.map((post, i) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className={`group block border border-[#E5E7EB] bg-white hover:border-[#0A0A0A] transition-all duration-200 ${i === 0 ? "md:col-span-2" : ""}`}
                data-testid={`blog-post-card-${post.slug}`}
              >
                {post.thumbnail_base64 && (
                  <div className={`overflow-hidden ${i === 0 ? "h-64" : "h-44"}`}>
                    <img
                      src={`data:image/png;base64,${post.thumbnail_base64}`}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex items-center gap-1 text-[11px] text-[#9CA3AF] font-mono">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.published_at || post.created_at)}
                    </span>
                    {post.tags?.slice(0, 2).map(tag => (
                      <span key={tag} className="flex items-center gap-1 text-[11px] bg-[#F3F4F6] text-[#6B7280] px-2 py-0.5">
                        <Tag className="w-2.5 h-2.5" />{tag}
                      </span>
                    ))}
                  </div>
                  <h2 className={`font-bold text-[#0A0A0A] group-hover:underline underline-offset-2 mb-2 ${i === 0 ? "text-2xl" : "text-lg"}`}>
                    {post.title}
                  </h2>
                  <p className="text-sm text-[#6B7280] leading-relaxed mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#0A0A0A] uppercase tracking-wider group-hover:gap-2 transition-all">
                    Read article <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] py-8 px-6 bg-white">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <LogoFull size={20} />
          <div className="flex items-center gap-6">
            <Link to="/" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Home</Link>
            <Link to="/changelog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Changelog</Link>
            <Link to="/register" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Sign up free</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
